import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'
import { Design_ } from './apiDesigns'
import { convertFromSEK } from 'src/shared/utils/currency'

export type InsertQuoteSubItem =
  Database['public']['Tables']['quote_sub_item']['Insert'] & {
    /** Links setup_cost rows to their decoration quote_sub_item */
    data?: { quote_sub_item_id?: number }
  }
export type UpdateQuoteSubItem =
  Database['public']['Tables']['quote_sub_item']['Update']
export type QuoteSubItem_ =
  Database['public']['Tables']['quote_sub_item']['Row'] & {
    design?: Array<Design_>
  }

export const useQuoteSubItemApi = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const currency = useOrganizationCurrency()

  const { data: quoteSubItems } = useQuery({
    queryKey: [queryKeys.quoteSubItems],
    queryFn: async () =>
      await supabase
        .from('quote_sub_item')
        .select('*')
        .order('created_at', { ascending: false }),
  })

  const createQuoteSubItem = useMutation({
    mutationFn: async (insertQuoteSubItem: InsertQuoteSubItem) => {
      const unit_price_sek =
        insertQuoteSubItem.unit_price != null
          ? convertFromSEK(insertQuoteSubItem.unit_price, currency)
          : insertQuoteSubItem.unit_price
      return await supabase
        .from('quote_sub_item')
        .insert({ ...insertQuoteSubItem, unit_price: unit_price_sek })
        .select('id')
        .single()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteSubItems] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
    },
  })

  const updateQuoteSubItem = useMutation({
    mutationFn: async ({ id, ...rest }: UpdateQuoteSubItem) => {
      const payload = { ...rest }
      if (rest.unit_price != null) {
        payload.unit_price = convertFromSEK(rest.unit_price, currency) as number
      }
      return await supabase.from('quote_sub_item').update(payload).eq('id', id!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteSubItems] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItemById] })
    },
  })

  const deleteQuoteSubItem = useMutation({
    mutationFn: async (id: number) => {
      await supabase.from('quote_sub_item').delete().eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteSubItems] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
    },
  })

  return {
    quoteSubItems,
    createQuoteSubItem,
    updateQuoteSubItem,
    deleteQuoteSubItem,
  }
}
